import { useEffect, useState } from "react";
import { apiSlice } from "../../../features/api/apiSlice";

/**
 * The National Policy Committee's closed forum.
 *
 * The gate is the server's: every endpoint below sits behind the
 * `policy.committee` middleware, which re-derives the committee from the
 * offices held today on each request. `useForumAccessQuery` is the one call
 * that is allowed to come back "no" — the portal uses it to decide whether
 * to show the module, and nothing more. Hiding the menu entry is a courtesy,
 * not the security.
 */

// ── Types ────────────────────────────────────────────────────────────────────

export type ForumPerson = {
  id: number;
  name: string;
  pro_pic?: string | null;
  /** The office they sit here by — "Past President", "Board of Trustees". */
  seat?: string | null;
  /** Fellow, Member, Probationer — from the member record. */
  grade?: string | null;
  /** Secretariat: here to moderate rather than by an office held. */
  is_admin?: boolean;
  /** Their chapter. Null for a national officer with none on file. */
  chapter?: string | null;
};

/**
 * The badge beside a name, and in this order.
 *
 * The office first: in this room a Past President speaking is not the same
 * as a name, and that is what the badge is for. Then the grade. "Admin"
 * last, because it only applies to someone with neither — the secretariat,
 * who are here to keep the place rather than by an office held.
 *
 * Here rather than on a page, because the index and the discussion itself
 * both draw this badge and a member who reads as one thing on one screen
 * and another on the next is a member nobody can place.
 */
export function badgeFor(person?: ForumPerson | null): string | null {
  return person?.seat || person?.grade || (person?.is_admin ? "Admin" : null);
}

/**
 * The categories a discussion can be filed under.
 *
 * A copy of App\Support\ForumCategories, in the same order. The server is
 * the authority — `useForumCategoriesQuery` returns this list with a count
 * on each — but the composer has to offer the choices before any of that
 * has loaded, and the sidebar has to render while the counts are in flight.
 * Keys must match the server's exactly; the labels are only ever displayed.
 */
export const FORUM_CATEGORIES: { key: string; label: string; blurb: string }[] = [
  { key: "practice", label: "Practice & Procurement", blurb: "Contracts, tendering, BoQs, final accounts" },
  { key: "costs", label: "Costs & Prices", blurb: "Market rates, inflation, Databank questions" },
  { key: "exams", label: "Exams & Membership", blurb: "TPC/GDE, interviews, CPD, upgrading" },
  { key: "software", label: "Software & BIM", blurb: "CostX, Revit, 5D, spreadsheets" },
  { key: "careers", label: "Careers & Mentoring", blurb: "Jobs, pupillage, YQSF" },
  { key: "general", label: "General", blurb: "Everything else" },
];

export type ForumCategory = {
  key: string;
  label: string;
  blurb: string;
  count: number;
};

/** The three tabs above the index. */
export type ForumSort = "latest" | "liked" | "unanswered";

export type ForumAttachment = {
  id: number;
  filename: string;
  mime_type: string;
  size: number;
  /** An API endpoint, not a storage link: it re-checks membership. */
  url: string;
};

export type ForumThreadSummary = {
  id: number;
  title: string;
  excerpt: string;
  author: ForumPerson | null;
  category: string;
  category_label: string;
  is_pinned: boolean;
  is_locked: boolean;
  replies_count: number;
  likes_count: number;
  liked_by_me: boolean;
  last_activity_at: string | null;
  created_at: string;
  is_unread: boolean;
  can_edit: boolean;
};

export type ForumPost = {
  id: number;
  /** The comment this one answers. Null for a comment on the discussion. */
  parent_id?: number | null;
  /** Answers to this comment. One level deep — never any deeper. */
  replies?: ForumPost[];
  body: string;
  author: ForumPerson | null;
  created_at: string;
  updated_at: string;
  likes_count: number;
  liked_by_me: boolean;
  can_edit: boolean;
  can_delete: boolean;
  attachments: ForumAttachment[];
};

export type ForumThreadDetail = {
  id: number;
  title: string;
  body: string;
  author: ForumPerson | null;
  category: string;
  category_label: string;
  is_pinned: boolean;
  is_locked: boolean;
  replies_count: number;
  likes_count: number;
  liked_by_me: boolean;
  created_at: string;
  updated_at: string;
  /** Where the reader had got to before this visit — draws the "new" line. */
  last_read_at: string | null;
  can_edit: boolean;
  can_moderate: boolean;
  attachments: ForumAttachment[];
  posts: ForumPost[];
};

export type ForumAccess = {
  allowed: boolean;
  can_moderate: boolean;
  seat: string | null;
};

export type ForumMemberGroup = {
  group: string;
  label: string;
  people: { name: string; seat: string }[];
};

type Paginated<T> = {
  data: T[];
  current_page: number;
  last_page: number;
  total: number;
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Send FormData when files are attached, plain JSON otherwise.
 *
 * Same shape as the support module's, and for the same reason: multipart on
 * every request would turn every reply into a file upload as far as the
 * 5-minute timeout in apiSlice is concerned.
 */
function buildBody(fields: Record<string, unknown>, files?: File[]) {
  if (!files?.length) return fields;

  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => {
    if (v == null) return;
    // PHP reads (bool)"false" as true, so booleans go over as "1"/"0".
    if (typeof v === "boolean") fd.append(k, v ? "1" : "0");
    else fd.append(k, String(v));
  });
  files.forEach((f) => fd.append("attachments[]", f));
  return fd;
}

// ── Endpoints ────────────────────────────────────────────────────────────────

export const forumApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /** Am I on the committee? The only endpoint outside the gate. */
    forumAccess: builder.query<ForumAccess, void>({
      query: () => "/forum/me",
      providesTags: ["ForumAccess"],
    }),

    forumMembers: builder.query<{ total: number; groups: ForumMemberGroup[] }, void>({
      query: () => "/forum/members",
      providesTags: ["ForumMembers"],
    }),

    /** The category sidebar, with a count against each. */
    forumCategories: builder.query<{ total: number; categories: ForumCategory[] }, void>({
      query: () => "/forum/categories",
      providesTags: ["ForumCategories"],
    }),

    forumThreads: builder.query<
      Paginated<ForumThreadSummary>,
      { q?: string; page?: number; category?: string | null; sort?: ForumSort } | void
    >({
      query: (args) => ({
        url: "/forum/threads",
        params: {
          q: args?.q || undefined,
          // Null is "all discussions", and an empty `category=` would be a
          // filter on the empty string rather than no filter at all.
          category: args?.category || undefined,
          sort: args?.sort && args.sort !== "latest" ? args.sort : undefined,
          page: args?.page || 1,
        },
      }),
      providesTags: ["ForumThreads"],
    }),

    forumThread: builder.query<ForumThreadDetail, number>({
      query: (id) => `/forum/threads/${id}`,
      providesTags: (_r, _e, id) => [{ type: "ForumThread" as const, id }],
    }),

    createForumThread: builder.mutation<
      ForumThreadDetail,
      { title: string; body: string; category: string; files?: File[] }
    >({
      query: ({ title, body, category, files }) => ({
        url: "/forum/threads",
        method: "POST",
        body: buildBody({ title, body, category }, files),
      }),
      invalidatesTags: ["ForumThreads", "ForumCategories"],
    }),

    // POST, not PATCH: the host in front of the API rejects PATCH outright.
    updateForumThread: builder.mutation<
      ForumThreadDetail,
      { id: number; title?: string; body?: string; category?: string; files?: File[] }
    >({
      query: ({ id, title, body, category, files }) => ({
        url: `/forum/threads/${id}`,
        method: "POST",
        body: buildBody({ title, body, category }, files),
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "ForumThreads",
        "ForumCategories",
        { type: "ForumThread" as const, id },
      ],
    }),

    deleteForumThread: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/forum/threads/${id}`, method: "DELETE" }),
      invalidatesTags: ["ForumThreads", "ForumCategories"],
    }),

    pinForumThread: builder.mutation<ForumThreadDetail, { id: number; pinned: boolean }>({
      query: ({ id, pinned }) => ({
        url: `/forum/threads/${id}/pin`,
        method: "POST",
        body: { pinned: pinned ? 1 : 0 },
      }),
      invalidatesTags: (_r, _e, { id }) => ["ForumThreads", { type: "ForumThread" as const, id }],
    }),

    lockForumThread: builder.mutation<ForumThreadDetail, { id: number; locked: boolean }>({
      query: ({ id, locked }) => ({
        url: `/forum/threads/${id}/lock`,
        method: "POST",
        body: { locked: locked ? 1 : 0 },
      }),
      invalidatesTags: (_r, _e, { id }) => ["ForumThreads", { type: "ForumThread" as const, id }],
    }),

    /**
     * Like or unlike a discussion.
     *
     * Deliberately invalidates NOTHING. Re-fetching the thread would mark it
     * read a second time and take the "new since your last visit" line with
     * it; re-fetching the index would re-sort the list under the reader's
     * finger on the "Most liked" tab. The two caches are patched in place
     * instead, and the server's own count is written over the guess when the
     * answer lands.
     */
    likeForumThread: builder.mutation<
      { liked: boolean; likes_count: number },
      { id: number; liked: boolean }
    >({
      query: ({ id, liked }) => ({
        url: `/forum/threads/${id}/like`,
        method: "POST",
        // The state being asked for, not "toggle": a tap re-sent after a
        // dropped response then settles as one like rather than undoing
        // the first.
        body: { liked: liked ? 1 : 0 },
      }),
      async onQueryStarted({ id, liked }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          forumApi.util.updateQueryData("forumThread", id, (draft) => {
            draft.liked_by_me = liked;
            draft.likes_count = Math.max(0, draft.likes_count + (liked ? 1 : -1));
          }),
        );

        try {
          const { data } = await queryFulfilled;
          dispatch(
            forumApi.util.updateQueryData("forumThread", id, (draft) => {
              draft.liked_by_me = data.liked;
              draft.likes_count = data.likes_count;
            }),
          );
        } catch {
          patch.undo();
        }
      },
    }),

    /** The same, for a reply. `threadId` is only ever used to find the cache. */
    likeForumPost: builder.mutation<
      { liked: boolean; likes_count: number },
      { id: number; threadId: number; liked: boolean }
    >({
      query: ({ id, liked }) => ({
        url: `/forum/posts/${id}/like`,
        method: "POST",
        body: { liked: liked ? 1 : 0 },
      }),
      async onQueryStarted({ id, threadId, liked }, { dispatch, queryFulfilled }) {
        const apply = (l: boolean, count?: number) =>
          dispatch(
            forumApi.util.updateQueryData("forumThread", threadId, (draft) => {
              const post = draft.posts.find((p) => p.id === id);
              if (!post) return;
              post.liked_by_me = l;
              post.likes_count = count ?? Math.max(0, post.likes_count + (l ? 1 : -1));
            }),
          );

        const patch = apply(liked);

        try {
          const { data } = await queryFulfilled;
          apply(data.liked, data.likes_count);
        } catch {
          patch.undo();
        }
      },
    }),

    replyToForumThread: builder.mutation<
      ForumPost,
      { threadId: number; body: string; parentId?: number | null; files?: File[] }
    >({
      query: ({ threadId, body, parentId, files }) => ({
        url: `/forum/threads/${threadId}/posts`,
        method: "POST",
        // A comment on the discussion itself sends no parent at all —
        // buildBody drops a null, and the server reads its absence as such.
        body: buildBody({ body, parent_id: parentId ?? undefined }, files),
      }),
      invalidatesTags: (_r, _e, { threadId }) => [
        "ForumThreads",
        { type: "ForumThread" as const, id: threadId },
      ],
    }),

    updateForumPost: builder.mutation<ForumPost, { id: number; threadId: number; body: string; files?: File[] }>({
      query: ({ id, body, files }) => ({
        url: `/forum/posts/${id}`,
        method: "POST",
        body: buildBody({ body }, files),
      }),
      invalidatesTags: (_r, _e, { threadId }) => [{ type: "ForumThread" as const, id: threadId }],
    }),

    deleteForumPost: builder.mutation<{ message: string }, { id: number; threadId: number }>({
      query: ({ id }) => ({ url: `/forum/posts/${id}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, { threadId }) => [
        "ForumThreads",
        { type: "ForumThread" as const, id: threadId },
      ],
    }),
  }),
});

export const {
  useForumAccessQuery,
  useForumMembersQuery,
  useForumCategoriesQuery,
  useForumThreadsQuery,
  useForumThreadQuery,
  useCreateForumThreadMutation,
  useUpdateForumThreadMutation,
  useDeleteForumThreadMutation,
  usePinForumThreadMutation,
  useLockForumThreadMutation,
  useReplyToForumThreadMutation,
  useUpdateForumPostMutation,
  useDeleteForumPostMutation,
  useLikeForumThreadMutation,
  useLikeForumPostMutation,
} = forumApi;

/**
 * A private attachment as something an <img> can show.
 *
 * Forum files are not on /storage — they are streamed by an authenticated
 * endpoint that re-checks committee membership — so `src={a.url}` sends no
 * bearer token and comes back 401. The bytes are fetched with the token,
 * turned into a blob URL, and released when the screen is done with them.
 *
 * Returns null while it is loading and if it fails, which is what the hero
 * wants: a discussion with no usable picture should render as a title, not
 * as a broken image.
 */
export function useForumImage(url?: string | null) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setSrc(null);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;
    const token = localStorage.getItem("token");

    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : undefined })
      .then((res) => (res.ok ? res.blob() : Promise.reject(new Error(String(res.status)))))
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });

    return () => {
      cancelled = true;
      // Revoked on the way out: a blob URL held open is the whole image
      // kept in memory for as long as the tab lives.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return src;
}

/** The picture a discussion leads with: its first image attachment, if any. */
export function coverAttachment(attachments?: ForumAttachment[] | null) {
  return attachments?.find((a) => a.mime_type?.startsWith("image/")) ?? null;
}

/**
 * Fetch a private attachment and hand it to the browser as a download.
 *
 * It cannot be an <a href> or an <img src>: the file is served by an
 * authenticated API route, and neither of those sends the bearer token. So
 * the bytes are fetched with the token, turned into a blob URL and released
 * straight after.
 */
export async function downloadForumAttachment(url: string, filename: string) {
  const token = localStorage.getItem("token");

  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (!res.ok) {
    throw new Error(
      res.status === 403
        ? "This file is open to the Policy Committee only."
        : "That file could not be opened.",
    );
  }

  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}
